import React, { useState, useRef, useEffect } from "react"
import { Grid, Box, Typography, TextField } from "@material-ui/core"
import moment from "moment-timezone"
import {
  UsersGroupsAssigner,
  usersGroupsEveryoneItem,
  MentionTextInput,
  MissingLocationAccessBlockquote,
  HasActiveJobsChangedBlockquote,
  HasActiveJobsScheduleChangedBlockquote,
} from ".."
import { useMutationCreateTask, PRIORITY, useLazyQueryActiveTaskJobs } from "../../data"
import { mapToIds, removeTypenames, toId, useDateUtils, useFormUtils } from "../../utils"
import { useAuth } from "../../services"
import { useMutationUpdateTask } from "../../data/jobs/useMutationUpdateTask"
import { CreatorActions, CreatorAtAndUploaders } from "../Creators"
import { UploadingImagesList, UploadingList } from "../Uploader"
import { DateTimeAssigner } from "../DateTimePickers"
import Checkbox from "../Checkbox"
import { TextDivider } from "../TextDivider"
import { ColumnBox } from "../Boxes"
import { LocationsAssigner } from "../Assigners"

const initialState = {
  name: null,
  description: "",
  priority: PRIORITY.STANDARD,
  after: null,
  due: null,
  pinned: false,
  before: null,
  users: [],
  groups: [],
  uploads: [],
}

const TaskCreator = ({ onClose, edit }) => {
  const [createTask, { loading: createTaskLoading }] = useMutationCreateTask()
  const [updateTask, { loading: updateTaskLoading }] = useMutationUpdateTask()
  const [loadActiveTaskJobs, activeTaskJobsResult] = useLazyQueryActiveTaskJobs()
  const [isInit, setIsInit] = useState(true)
  const { isValid, isError, getErrorMessage, debounceTouched } = useFormUtils()
  const { scheduleDateToMoment, momentToScheduleDate } = useDateUtils()
  const {
    settings: { locations: userLocations },
  } = useAuth()

  const [showAssigner] = useState(false)
  const descriptionRef = useRef(null)

  const [nameTouched, setNameTouched] = useState(false)
  const [scheduleChanged, setScheduleChanged] = useState(false)

  const [name, setName] = useState(initialState.name)
  const [description, setDescription] = useState(initialState.description)
  const [priority, setPriority] = useState(initialState.priority)
  const [after, setAfter] = useState(initialState.after)
  const [due, setDue] = useState(initialState.due)
  const [pinned, setPinned] = useState(initialState.pinned)
  const [before] = useState(initialState.before)
  const [hasEveryone, setHasEveryone] = useState(false)
  const [locations, setLocations] = useState(null)
  const [users, setUsers] = useState(initialState.users)
  const [groups, setGroups] = useState(initialState.groups)
  const [uploads, setUploads] = useState(initialState.uploads)
  const [uploading, setUploading] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [initialPicked, setInitialPicked] = useState([])

  useEffect(() => {
    if (isInit) {
      if (edit) {
        loadActiveTaskJobs({ variables: { task: edit.id } })

        const noUsersOrGroups = !edit.users.length && !edit.groups.length
        setName(edit.name)
        setDescription(edit.description)
        setPriority(edit.priority)
        setAfter(edit.after)
        setDue(edit.due)
        setPinned(edit.pinned)
        setHasEveryone(noUsersOrGroups)
        setLocations([
          ...edit.locations.map((location) => {
            const item = userLocations.find((ul) => toId(ul) === toId(location))
            if (item) return item
            return { id: toId(location), name: "(Hidden)", noAccess: true }
          }),
        ])
        setUsers(edit.users.map((u) => u.id))
        setGroups(edit.groups.map((g) => g.id))
        setUploads(edit.uploads)
        setInitialPicked(noUsersOrGroups ? [usersGroupsEveryoneItem] : [...edit.users, ...edit.groups])
      } else {
        setLocations(userLocations.length > 1 ? [] : [userLocations[0]])
      }
      setIsInit(false)
    }
  }, [isInit, edit, userLocations, loadActiveTaskJobs])

  const handleOnClose = (event, isCancel) => {
    handleResetState()
    onClose(isCancel)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (formValid()) {
      const uploadIds = uploads.map((upload) => upload.id)
      const variables = {
        locations: locations.map((location) => toId(location)),
        name,
        description,
        priority,
        after: removeTypenames(after),
        due: removeTypenames(due),
        pinned,
        before,
        users,
        groups,
        uploads: uploadIds,
      }
      if (edit) {
        await updateTask({
          variables: {
            id: edit.id,
            ...variables,
          },
        })
      } else {
        await createTask({
          variables,
        })
      }
      handleOnClose()
    }
  }

  const handleResetState = () => {
    setName(initialState.name)
    setPriority(initialState.priority)
    setAfter(initialState.after)
    setPinned(initialState.pinned)
    setUsers(initialState.users)
    setGroups(initialState.groups)
    setLocations(null)
  }

  const handleLocationsPickedChanged = (picked) => {
    setLocations([...picked.map((location) => toId(location))])
  }

  const handleUsersGroupsPickedChanged = (picked) => {
    const newHasEveryone = picked.find((p) => p.__typename === "Everyone")
    setHasEveryone(newHasEveryone)
    setUsers(newHasEveryone ? [] : [...mapToIds(picked.filter((p) => p.__typename === "User"))])
    setGroups(newHasEveryone ? [] : [...mapToIds(picked.filter((p) => p.__typename === "Group"))])
  }

  const handleAfterChanged = (newAfter) => {
    if (!moment(newAfter).isSame(scheduleDateToMoment(after), "day")) setScheduleChanged(true)
    setAfter(momentToScheduleDate(newAfter))
  }

  const handleDueChanged = (newDue) => setDue(momentToScheduleDate(newDue))

  const handleAtClick = () => {
    setDescription(name ? `${description.trimEnd()} @` : "@")
    descriptionRef.current.focus()
  }

  const handleBeforeUpload = (items) => {
    setIsUploading(true)
    setUploading(() => items)
  }
  const handleUploadProgress = (items) => {
    setUploading(() => items)
  }
  const handleItemUploaded = (item) => {
    setUploads((state) => [...state, item])
  }
  const handleRemoveUpload = (id) => {
    setUploads([...uploads.filter((upload) => upload.id !== id)])
  }
  const handleNotUploading = () => {
    setIsUploading(false)
  }
  const handlePinnedChange = () => {
    setPinned(!pinned)
  }

  const formValid = () => isValid(name, after) && (hasEveryone || users.length || groups.length)

  const isFormValid = formValid()

  const assignToDisabled = !locations || !locations.length

  const missingLocationAccess =
    edit && locations && locations.some((item) => !userLocations.some((ul) => toId(ul) === toId(item)))

  const activeJobCount = activeTaskJobsResult.data && activeTaskJobsResult.data.activeTaskJobs.length

  return (
    <>
      <MissingLocationAccessBlockquote show={missingLocationAccess} subject="task" />

      <HasActiveJobsChangedBlockquote
        show={activeJobCount > 0 && !missingLocationAccess}
        subject="task"
        count={activeJobCount}
      />

      <Box mb={2}>
        <TextField
          label="What's the task?"
          variant="outlined"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          onBlur={() => debounceTouched(setNameTouched)}
          error={isError(name, nameTouched)}
          helperText={getErrorMessage(name, nameTouched, "Please enter the task")}
          inputProps={{ "data-cy": "TextField-title" }}
        />
      </Box>
      <MentionTextInput
        inputRef={descriptionRef}
        variant="outlined"
        label="Description"
        height={80}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        fullWidth
        multiline
        data-cy="TextField-description"
      />
      {uploads && uploads.length > 0 && (
        <Box my={1}>
          <UploadingImagesList
            uploaded={uploads}
            uploading={uploading}
            onRemoveUpload={handleRemoveUpload}
            height={100}
            gridItemProps={{
              xs: 3,
            }}
          />
          <UploadingList uploaded={uploads} uploading={uploading} images={false} onRemoveUpload={handleRemoveUpload} />
        </Box>
      )}
      <Box mb={2}>
        <Grid item sm={3} xs={12}>
          <CreatorAtAndUploaders
            onAtClick={handleAtClick}
            onBeforeUpload={handleBeforeUpload}
            onProgress={handleUploadProgress}
            onItemUploaded={handleItemUploaded}
            onAfterUpload={handleNotUploading}
            onError={handleNotUploading}
            disableUploaders={isUploading}
          />
        </Grid>
      </Box>
      <Box mb={1}>
        {locations && userLocations.length > 1 && (
          <LocationsAssigner
            initialPicked={locations}
            onPickedChanged={handleLocationsPickedChanged}
            bottomBorder={false}
            cy="TaskCreator-locations"
          />
        )}
        {!isInit && (
          <UsersGroupsAssigner
            title="Assign to"
            placeholder={assignToDisabled ? "No locations selected" : "Nobody selected"}
            allowSelf
            locations={locations ? locations.map((location) => toId(location)) : []}
            openPicker={showAssigner}
            initialPicked={initialPicked}
            onPickedChanged={handleUsersGroupsPickedChanged}
            disabled={assignToDisabled}
            cy="TaskCreator-to"
          />
        )}
        <DateTimeAssigner
          value={after}
          dueValue={due}
          timeLabel="Available from"
          dueLabel="Due at (optional)"
          emptyTimeLabel="All day"
          emptyDueLabel="None"
          timeDisplayPrefix="Start at "
          onChange={handleAfterChanged}
          onDueChange={handleDueChanged}
          topBorder={false}
          hasDue
        />
      </Box>
      <ColumnBox mt={0} mb={2} px={1}>
        <Checkbox type="label" label="Pin this job" checked={pinned} onChange={handlePinnedChange} />
        <Typography variant="caption">
          If pinned, this job will remain at the top of each day until completed
        </Typography>
      </ColumnBox>

      <TextDivider mb={2} />

      <HasActiveJobsScheduleChangedBlockquote
        show={activeJobCount > 0 && scheduleChanged}
        subject="task"
        count={activeJobCount}
      />

      <CreatorActions
        id="TaskCreator-CreatorActions"
        subject="Task"
        onClose={handleOnClose}
        onSubmit={handleSubmit}
        disableSubmit={!isFormValid || isUploading}
        submitLoading={createTaskLoading || updateTaskLoading}
      />
    </>
  )
}

export default TaskCreator
